import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axiosInstance from "../api/axiosInstance";
import { delay } from "../helper/delayresponse";

interface SmtpGateways {
  id?: number;
  gateway_name?: string;
  client_id?: string;
  client_secret?: string;
  refresh_token?: string;
  email?: string;
  smtp_gateway?: number;
  from_email?: string;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string;
}

interface SmtpGatewaysState {
  smtpgatewaysdata: SmtpGateways[];
  info: {
    count: number;
    current_page: number;
    limit: number;
    total_page: number;
  } | null;
  error: string | null;
  loading: boolean;
}

// ====================listing-action-start==============
export const GetallSmtpGateways = createAsyncThunk<
  {
    data: SmtpGateways[];
    info: {
      count: number;
      current_page: number;
      limit: number;
      total_page: number;
    };
  },
  { search: string; page: number; limit: number },
  { rejectValue: string }
>("GetallSmsGateways", async ({ search, page, limit }, { rejectWithValue }) => {
  try {
    await delay(1000)
    const response = await axiosInstance.get(`api/admin/smtp-gateways`, {
      params: { search, page, limit },
    });
    return { data: response.data.data, info: response.data.info };
  } catch (error: any) {
    if (error.response && error.response.data) {
      return rejectWithValue(error.response.data);
    }
    return rejectWithValue(error.message);
  }
});
// ====================listing-action-end==============

// // ==================view-Smtpgatewaylist-api-start=============
// export const GetSmtpGatewayView = createAsyncThunk<
//     SmtpGateways,
//     number,
//     { rejectValue: string }
// >(
//     'smtpgatewaysdata/GetSmtpGatewayView',
//     async (id, { rejectWithValue }) => {
//         try {
//             const response = await axiosInstance.get(`/api/admin/smtp-gateways/${id}`);
//             return response.data.data;
//         } catch (error: any) {
//             if (error.response && error.response.data) {
//                 return rejectWithValue(error.response.data);
//             }
//             return rejectWithValue(error.message);
//         }
//     }
// );
// // ==================view-Smtpgatewaylist-api-end============

// ==================AddSmtpgateways-action-start===========
export const AddSmtpGateways = createAsyncThunk<
  SmtpGateways,
  SmtpGateways,
  { rejectValue: string }
>(
  "smtpgatewaysdata/AddSmtpGateways",
  async (smtpGatewaysData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `api/admin/smtp-gateways/create`,
        smtpGatewaysData
      );
      return response.data;
    } catch (error: any) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error.message);
    }
  }
);
// ==================AddSmtpgateways-action-end===========

// =================Update-smtp-action-start=========
export const UpdateSmtpgateways = createAsyncThunk<
  SmtpGateways,
  { id: number; updatedData: SmtpGateways },
  { rejectValue: string }
>(
  "smtpgatewaysdata/UpdateSmtpgateways",
  async ({ id, updatedData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `api/admin/smtp-gateways/update/${id}`,
        updatedData
      );
      return response.data;
    } catch (error: any) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error.message);
    }
  }
);
// =================Update-smtp-action-end=========

// ================Delete-smtpgateway-start=========
export const Deletesmtpgateways = createAsyncThunk<
  { id: number },
  { id: number }
>("smtpgatewaysdata/Deletesmtpgateways", async ({ id }) => {
  try {
    const { data } = await axiosInstance.delete(
      `api/admin/smtp-gateways/${id}`
    );
    return data;
  } catch (error: any) {
    if (error.response && error.response.data) {
      return error.response.data;
    }
    return error.message;
  }
});
// ================Delete-smtpgateway-end=========

const initialState: SmtpGatewaysState = {
  smtpgatewaysdata: [],
  info: null,
  error: null,
  loading: false,
};

const smtpgatewaysSlice = createSlice({
  name: "Smtpgateways",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // ===============get-all-smtp-slice-start============
      .addCase(GetallSmtpGateways.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        GetallSmtpGateways.fulfilled,
        (
          state,
          action: PayloadAction<{
            data: SmtpGateways[];
            info: {
              count: number;
              current_page: number;
              limit: number;
              total_page: number;
            };
          }>
        ) => {
          state.loading = false;
          state.smtpgatewaysdata = action.payload.data;
          state.info = action.payload.info;
        }
      )
      .addCase(
        GetallSmtpGateways.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error = action.payload || "An error occurred";
        }
      );
    // ===============get-all-smtp-slice-end============

    // =================add-=smtp-slice-start============
    builder
      .addCase(AddSmtpGateways.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        AddSmtpGateways.fulfilled,
        (state, action: PayloadAction<SmtpGateways>) => {
          state.loading = false;
          state.smtpgatewaysdata.push(action.payload);
        }
      )
      .addCase(
        AddSmtpGateways.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error = action.payload || "Failed to add smtpgateways";
        }
      );
    // =================add-=smtp-slice-end============

    // =================Update-smtp-slice-start============
    builder
      .addCase(UpdateSmtpgateways.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        UpdateSmtpgateways.fulfilled,
        (state, action: PayloadAction<SmtpGateways>) => {
          state.loading = false;
          const index = state.smtpgatewaysdata.findIndex(
            (item) => item.id === action.payload.id
          );
          if (index !== -1) {
            state.smtpgatewaysdata[index] = action.payload;
          }
        }
      )
      .addCase(
        UpdateSmtpgateways.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error = action.payload || "Failed to update SmtpGateways";
        }
      );
    // =================Update-smtp-slice-end============

    // =================Delete-smtp-slice-start============
    builder
      .addCase(Deletesmtpgateways.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        Deletesmtpgateways.fulfilled,
        (state, action: PayloadAction<{ id: number }>) => {
          state.loading = false;
        }
      )
      .addCase(Deletesmtpgateways.rejected, (state, action: any) => {
        state.loading = false;
        state.error = action.payload;
      });
    // =================Delete-smtp-slice-end============
  },
});

export default smtpgatewaysSlice.reducer;
